import {OktaAuth} from '@okta/okta-auth-js';
import {RxdbRepository} from '../rxdb.repository';
import {Inject, Injectable} from '@angular/core';
import {OKTA_AUTH} from '@okta/okta-angular';
import {getEmployee} from '../../store';
import {Store} from '@ngrx/store';
import {Employee} from '../../models/employee/employee.model';
import {Subscription} from 'rxjs';
import {QuoteCollectionCheckpointAction} from '../../store/standalone';
import {bearerFetch, logUpsertErrors, markPulled, shouldPull, watchPending, withTabLock} from './multi-tab-sync';

// un devis offline est créé avec reference: null : $exists: false seul ne le trouverait pas
export const PENDING_QUOTES_SELECTOR = {
    isSynchronized: {$ne: true},
    $or: [
        {reference: {$exists: false}},
        {reference: null}
    ]
};

export const QUOTES_CHECKPOINT_KEY = 'epp.sync.quotes.checkpoint';
const PULL_BATCH_SIZE = 10;

/**
 * Synchronisation des devis (mode CRM linked) identique dans tous les onglets :
 * pas de réplication RxDB (liée à l'onglet leader), mais un push/pull sérialisé par Web Locks.
 */
@Injectable({
    providedIn: 'root'
})
export class QuoteCollectionSynchronizer {

    private readonly collectionName: string;
    private employee: Employee;
    private pushSubscription: Subscription;

    public constructor(public readonly store: Store,
        private rxdbRepository: RxdbRepository,
        @Inject(OKTA_AUTH) private _oktaAuth: OktaAuth) {
        this.collectionName = 'quotes';
        this.store.select(getEmployee).subscribe(employee => {
            this.employee = employee;
        });
    }

    async startPush(): Promise<void> {
        if (this.pushSubscription && !this.pushSubscription.closed) {
            return;
        }
        const db = await this.rxdbRepository.initDb();
        console.log(`Starting HTTP push for [${this.collectionName}]`);
        this.pushSubscription = watchPending(db[this.collectionName], PENDING_QUOTES_SELECTOR, () => this.pushPending());
    }

    stopPush(): void {
        this.pushSubscription?.unsubscribe();
        this.pushSubscription = undefined;
    }

    /**
     * Pousse tous les devis offline en attente. Un seul onglet à la fois : l'état est relu sous le lock,
     * donc un devis déjà poussé par un autre onglet n'est jamais re-POSTé.
     */
    async pushPending(): Promise<void> {
        await withTabLock(`epp-${this.collectionName}-push`, async () => {
            const db = await this.rxdbRepository.initDb();
            const collection = db[this.collectionName];
            const pendingDocs = await collection.find({selector: PENDING_QUOTES_SELECTOR}).exec();
            for (const pendingDoc of pendingDocs) {
                await this.pushQuote(collection, pendingDoc.toMutableJSON());
            }
        });
    }

    private async pushQuote(collection: any, doc: any): Promise<void> {
        try {
            // le serveur fait foi : si le devis a déjà été créé (ramené par le pull sous son id serveur),
            // on ne le recrée pas et on supprime simplement le doc offline orphelin
            const alreadySynchronizedDoc = await collection.findOne({
                selector: {
                    offlineId: doc.id,
                    _id: {$ne: doc._id},
                    reference: {$exists: true, $ne: null}
                }
            }).exec();
            if (alreadySynchronizedDoc) {
                await this.removeOfflineDoc(collection, doc._id);
                return;
            }

            const response = await bearerFetch('/apiQuoteManager/v1/quotes', this._oktaAuth, {
                method: 'POST',
                body: JSON.stringify({...doc, offlineId: doc.id})
            });

            const serverResponse = await response.json();
            const serverDoc = await collection.findOne(serverResponse?.id).exec();
            if (!serverDoc) {
                const newDoc = {
                    ...doc,
                    _id: serverResponse?.id,
                    id: serverResponse?.id,
                    isSynchronized: true,
                    reference: serverResponse?.reference,
                    creationDate: serverResponse?.creationDate
                };
                try {
                    await collection.insert(newDoc);
                } catch (insertError) {
                    // le pull a inséré la version serveur entre-temps : elle fait foi
                    if (insertError?.code !== 'CONFLICT') {
                        throw insertError;
                    }
                }
            }

            await this.removeOfflineDoc(collection, doc._id);
        } catch (err) {
            console.error('Error pushing quote to HTTP DB:', err);
            throw err;
        }
    }

    private async removeOfflineDoc(collection: any, offlineDocId: string): Promise<void> {
        const existingDoc = await collection.findOne(offlineDocId).exec();
        if (existingDoc) {
            await existingDoc.remove();
            await collection.cleanup(0);
        }
    }

    /**
     * Ramène les devis modifiés côté serveur depuis le checkpoint partagé entre onglets.
     * mustWait : attendre un éventuel pull en cours dans un autre onglet et ignorer le throttle
     * (premier sync) ; sinon le pull est simplement sauté si un autre onglet s'en occupe.
     */
    async pull(mustWait: boolean, storeCheckpoint: string): Promise<void> {
        try {
            await withTabLock(`epp-${this.collectionName}-pull`, async () => {
                if (!mustWait && !shouldPull(this.collectionName)) {
                    return;
                }
                const db = await this.rxdbRepository.initDb();
                const collection = db[this.collectionName];
                let checkpoint = this.readCheckpoint(storeCheckpoint);
                let hasMore = true;
                while (hasMore) {
                    const serverDocs = await this.fetchQuotesPage(checkpoint);
                    const previousCheckpoint = checkpoint;
                    if (serverDocs.length > 0) {
                        const newDocs = serverDocs.map(({id, ...rest}) => ({...rest, _id: id, id: id}));
                        logUpsertErrors(this.collectionName, await collection.bulkUpsert(newDocs));
                        checkpoint = this.saveCheckpoint(this.maxCheckPointDate(serverDocs));
                    }
                    // garde-fou : sans avancée du checkpoint, une page pleine bouclerait indéfiniment
                    hasMore = serverDocs.length === PULL_BATCH_SIZE && checkpoint !== previousCheckpoint;
                }
                markPulled(this.collectionName);
            }, {ifAvailable: !mustWait});
        } catch (error) {
            console.warn(`Error during HTTP pull for [${this.collectionName}] :`, error.message);
        }
    }

    private async fetchQuotesPage(checkpoint: string): Promise<any[]> {
        const params = new URLSearchParams();
        params.append('quoteCollectionCheckPoint', checkpoint);
        params.append('size', PULL_BATCH_SIZE.toString());
        const url = `/apiQuoteManager/v1/quotes/employee/${encodeURIComponent(this.employee.employeeNumber)}?${params.toString()}`;
        const response = await bearerFetch(url, this._oktaAuth, {method: 'GET'});
        return (await response.json()) || [];
    }

    private maxCheckPointDate(serverDocs: any[]): string {
        return serverDocs.reduce(
            (max: string, q: any) => toDate(q.checkPointDate) > toDate(max) ? q.checkPointDate : max,
            serverDocs[0].checkPointDate
        );
    }

    /**
     * Le checkpoint est stocké dans une clé localStorage dédiée, relue à chaque pull :
     * le state ngrx persisté est propre à chaque onglet et peut être écrasé par un autre onglet.
     */
    private readCheckpoint(storeCheckpoint: string): string {
        const shared = localStorage.getItem(QUOTES_CHECKPOINT_KEY);
        if (!shared) {
            return storeCheckpoint;
        }
        if (!storeCheckpoint) {
            return shared;
        }
        return toDate(shared) >= toDate(storeCheckpoint) ? shared : storeCheckpoint;
    }

    private saveCheckpoint(checkpoint: string): string {
        const current = localStorage.getItem(QUOTES_CHECKPOINT_KEY);
        const next = current && toDate(current) > toDate(checkpoint) ? current : checkpoint;
        localStorage.setItem(QUOTES_CHECKPOINT_KEY, next);
        this.store.dispatch(new QuoteCollectionCheckpointAction(next as any));
        return next;
    }

}

function toDate(checkPointDate: string): Date {
    return new Date(checkPointDate.replace(' ', 'T'));
}
